import React, {useCallback, useEffect, useMemo, useState} from "react";
import * as Yup from "yup";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import useIsMounted from "../../../hooks/use-is-mounted";
import {Col, Row} from "reactstrap";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Button from "../../base/button/button";
import {Dialog, DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import {createUUId} from "../../../../core/services/utils/utils";
import Api from "../../../../core/services/api/api";


const EditPrinterHubDialog = ({
                                  open,
                                  onClose,
                                  onEdit,
                                  hub,
                              }) => {
    const [submitting, setSubmitting] = useState(false);
    const [initialState, setInitialState] = useState({});
    const isMounted = useIsMounted();
    const formId = useMemo(() => createUUId(), []);

    const schema = useMemo(() => Yup.object().shape({
        name: Yup.string().trim().nullable().required(ValidateMessages.required),
    }), []);
    const validate = useMemo(() => makeValidate(schema), [schema]);
    const required = useMemo(() => makeRequired(schema), [schema]);

    /**
     * With each change of [hub]:
     * - set initial state with [hub]'s data.
     */
    useEffect(() => {
        if (!open)
            return;
        setInitialState({
            name: hub?.name ?? '',
        })
    }, [hub?.name, open])

    /**
     * Edit printer hub.
     * @type {(function(*): Promise<void>)}
     */
    const editPrintHub = useCallback(async (values) => {
        setSubmitting(true);
        const forApi = {
            name: values.name,
            partnerHubId: hub?.id,
        }
        const response = await Api.updatePrinterHub(forApi);
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            onEdit({
                ...hub,
                name: forApi.name,
            });
            onClose();
        }
    }, [hub, isMounted, onClose, onEdit]);


    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth={'xs'}
                fullWidth
            >
                <DialogTitle>
                    Edit printer hub
                </DialogTitle>
                <DialogContent>
                    <Form
                        className='d-flex flex-wrap w-100 align-items-start mt-4'
                        onSubmit={editPrintHub}
                        validate={validate}
                        id={formId}
                        initialValues={initialState}
                        render={({values, form}) => {
                            return (
                                <>
                                    <Col xs={12} className={'p-0 mb-3'}>
                                        <OutlinedInputForm
                                            fullWidth
                                            name={'name'}
                                            placeholder={'name...'}
                                            label={"Name"}
                                            required={required.name}
                                            value={values.name ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                </>
                            );
                        }}
                    />
                </DialogContent>
                <DialogActions className={'p-0 m-0'}>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-end align-items-start w-100'>
                            <Button
                                disabled={submitting}
                                variant={'dialog'}
                                color={'primary'}
                                type={'submit'}
                                form={formId}
                                borderRadius={'8px 0 8px 0'}
                            >
                                {
                                    !submitting
                                        ? "Edit"
                                        : "Editing..."
                                }
                            </Button>
                        </Row>
                    </Row>
                </DialogActions>
            </Dialog>
        </>
    );

}

export default EditPrinterHubDialog;
